import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import axiosInstance from "../config/axios-config";
import {profileDraft, profilePayload, validateProfile} from "./distributor-profile";

async function persistProfile(tenantId, draft, current, language) {
    const options = {signal: current.controller.signal, headers: {"Language-Context": (language || "en").toUpperCase()}};
    // Refresh unedited fields (including working hours) before sending the full tenant PUT.
    const {data: latest} = await axiosInstance.get(`/api/v1/tenants/${tenantId}`, options);
    if (!current.active) return undefined;
    if (latest.id !== tenantId) throw new Error("ProfileIdentityMismatch");
    const {data} = await axiosInstance.put(`/api/v1/tenants/${tenantId}`, profilePayload(latest, draft), options);
    if (data?.id !== tenantId) throw new Error("ProfileIdentityMismatch");
    return data;
}

// Keep draft state and the request generation together so a tenant change invalidates the whole save.
export default function useTenantProfile(tenantId, distributor, onSaved) {
    const {t, i18n} = useTranslation();
    const [draft, setDraft] = useState(() => profileDraft(distributor));
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [notice, setNotice] = useState(null);
    const scope = useRef(null);
    const dirty = editMode && JSON.stringify(draft) !== JSON.stringify(profileDraft(distributor));

    useEffect(() => {
        const current = {active: true, pending: false, controller: new AbortController()};
        scope.current = current;
        setEditMode(false);
        setSaving(false);
        setErrors({});
        setNotice(null);
        return () => { current.active = false; current.controller.abort(); };
    }, [tenantId]);

    useEffect(() => {
        if (!editMode) setDraft(profileDraft(distributor));
    }, [distributor, editMode]);

    const toggleEdit = () => {
        if (scope.current?.pending) return;
        setDraft(profileDraft(distributor));
        setErrors({});
        setNotice(null);
        setEditMode(value => !value);
    };

    const save = async () => {
        const current = scope.current;
        if (!current?.active || current.pending || !editMode) return;
        const invalid = validateProfile(draft);
        setErrors(invalid);
        if (Object.keys(invalid).length) return;
        current.pending = true;
        setSaving(true);
        setNotice(null);
        try {
            const data = await persistProfile(tenantId, draft, current, i18n.language);
            if (!current.active) return;
            onSaved(data);
            setEditMode(false);
            setNotice({severity: "success", message: t("ProfileSaved")});
        } catch (error) {
            const fallback = error.message === "ProfileIdentityMismatch" ? "ProfileIdentityMismatch" : "ProfileSaveFailed";
            if (current.active) setNotice({severity: "error", message: error.response?.data?.message || t(fallback)});
        } finally {
            current.pending = false;
            if (current.active) setSaving(false);
        }
    };

    return {draft, editMode, saving, errors, notice, dirty, toggleEdit, save, setDraft};
}
